<template>
    <div class="work-experience">
        <C1FormGroup>
            <C1Typeahead
                id="jobTitle"
                ref="jobTitle"
                v-model="form.job_title"
                :label="$t('label.job_title')"
                label-for="jobTitle"
                :options="jobTitleOptions"
                name="jobTitle"
                :placeholder="$t('label.job_title_placeholder')"
                label-key="label"
                value-key="job_title_id"
                filter-by="index"
                hide-input-icon
                allow-new
                @search="jobTitleSearch"
            />
        </C1FormGroup>

        <C1FormGroup>
            <div class="afu-flex afu-items-center afu-justify-between">
                <C1Label label-for="occupation">
                    {{ $t('label.occupation') }}
                </C1Label>

                <C1WhatIsThis
                    :primary-text="$t('label.what_is_this_occupation')"
                    class="afu-mb-2"
                />
            </div>

            <C1ClsSelect
                id="occupation"
                v-model="occupationQuery"
                :options="occupationOptions"
                :placeholder="$t('label.work_occupation_placeholder')"
                :search-loading="searchOccupationLoading"
                is-single-select
                @search="searchOccupations"
                @hit="onOccupationChange"
            />
        </C1FormGroup>

        <C1FormGroup>
            <C1Typeahead
                id="employerName"
                v-model="form.employer_name"
                :label="$t('label.company')"
                label-for="employerName"
                :options="brandOptions"
                data-vv-as="Company"
                data-vv-name="employerName"
                name="employerName"
                :placeholder="$t('label.company_placeholder')"
                label-key="name"
                value-key="id"
                filter-by="name"
                hide-input-icon
                allow-new
                @hit="onCompanySelected"
                @search="brandSearch"
            />
        </C1FormGroup>

        <div class="afu-grid md:afu-grid-cols-2 afu-gap-8">
            <C1FormGroup>
                <C1Select
                    id="jobType"
                    v-model="form.job_type"
                    :label="$tc('label.job_type', 1)"
                    label-for="jobType"
                    :options="jobType"
                    data-vv-as="Job type"
                    data-vv-name="jobType"
                    label-key="value"
                    value-key="id"
                    :placeholder="$t('label.select_value', {value: $tc('label.job_type', 1)})"
                    hide-input-icon
                />
            </C1FormGroup>

            <C1FormGroup>
                <C1Select
                    v-model="form.contract_type"
                    :label="$tc('label.contract_type', 1)"
                    label-for="contractType"
                    :options="contractType"
                    data-vv-as="Contract type"
                    data-vv-name="contractType"
                    label-key="value"
                    value-key="id"
                    :placeholder="$t('label.select_value', {value: $tc('label.contract_type', 1)})"
                />
            </C1FormGroup>
        </div>

        <C1FormGroup>
            <C1Typeahead
                v-model="form.location"
                :label="$t('label.work_location')"
                label-for="workLocation"
                :options="locationOptions"
                :search-min-length="2"
                data-vv-as="Work location"
                data-vv-name="workLocation"
                :placeholder="$t('label.suburb_city_area')"
                label-key="label"
                @hit="onLocationSelected"
                @search="locationSearch"
            >
                <template v-slot:icon>
                    <FontAwesomeIcon
                        :icon="['far', 'map-marker-alt']"
                        class="afu-text-neutral-dark"
                    />
                </template>

                <template v-slot:noResults>
                    {{ $t('profile.not_found_global_location') }}
                </template>
            </C1Typeahead>
        </C1FormGroup>

        <C1FormGroup>
            <div class="afu-flex afu-items-center afu-justify-between">
                <C1Label label-for="startDate">
                    {{ $t('label.start_end_dates') }}
                </C1Label>

                <div class="afu-flex afu-items-center afu-text-sm afu-mb-2">
                    <span class="afu-mr-2">{{ $t('label.current_role') }}</span>

                    <C1AppleSwitch
                        v-model="form.is_current"
                        size="xs"
                    />
                </div>
            </div>

            <div class="afu-grid md:afu-grid-cols-2 afu-gap-2">
                <C1FormInput
                    id="startDate"
                    v-model="form.start_date"
                    data-vv-as="Start date"
                    data-vv-name="startDate"
                    type="month"
                    :placeholder="$t('label.select_value', {value: $t('label.start_date')})"
                    :max="form.end_date || today"
                />

                <C1FormInput
                    v-if="!form.is_current"
                    id="endDate"
                    key="end-date-picker"
                    v-model="form.end_date"
                    data-vv-as="End date"
                    data-vv-name="endDate"
                    type="month"
                    :placeholder="$t('label.select_value', {value: $t('label.end_date')})"
                    :min="form.start_date"
                    :max="today"
                />

                <C1FormInput
                    v-else
                    key="end-date-progress"
                    type="text"
                    disabled
                    :placeholder="$t('label.current_role')"
                />
            </div>
        </C1FormGroup>

        <div
            v-if="showBasePay"
            class="afu-grid md:afu-grid-cols-2 afu-gap-8"
        >
            <C1FormGroup>
                <div class="afu-flex afu-items-center afu-justify-between">
                    <C1Label>
                        {{ $t('label.base_pay') }}
                    </C1Label>

                    <PayBandMenu
                        v-model="payType"
                        :hide="payBandMenuHide"
                        @input="onPayTypeSelect"
                    />
                </div>

                <C1Select
                    v-if="payType === 'Annually'"
                    v-model="form.pay_band"
                    :options="payBandAnnuallyFiltered"
                    data-vv-name="payBand"
                    data-vv-as="Base pay"
                    label-key="label"
                    value-key="id"
                    :placeholder="$t('label.payband_placehoder')"
                />

                <C1Select
                    v-if="payType === 'Hourly'"
                    v-model="form.pay_band"
                    :options="payBandHourlyFiltered"
                    data-vv-name="payBand"
                    data-vv-as="Base pay"
                    label-key="label"
                    value-key="id"
                    :placeholder="$t('label.payband_placehoder')"
                />
            </C1FormGroup>

            <p class="work-experience__text">
                {{ $t('profile.pay_info') }}
            </p>
        </div>

        <C1FormGroup v-if="!hideDescription">
            <C1FormTextarea
                id="description"
                label="Description"
                label-for="description"
                v-model="form.description"
                name="description"
                :placeholder="$t('label.enter_value', {value: $t('label.description')})"
            />
        </C1FormGroup>
    </div>
</template>

<script>
    import { mapActions, mapMutations } from 'vuex';
    import has from 'lodash/has';
    import moment from 'moment/src/moment';
    import numeral from 'numeral';
    import PayBandMenu from '@/apps/dashboard/001/components/PayBandMenu';
    import C1WhatIsThis from '@/components/C1WhatIsThis';
    import C1FormTextarea from '@/components/C1Components/C1FormTextarea';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Label from '@/components/C1Components/C1Label';
    import C1Select from '@/components/C1Components/C1Select';
    import C1Typeahead from '@/components/C1Components/C1Typeahead';
    import C1ClsSelect from '@/components/C1Components/C1ClsSelect';
    import C1AppleSwitch from '@/components/C1Components/C1AppleSwitch';

    export default {
        name: 'WorkExperienceForm',

        components: {
            C1ClsSelect,
            C1Typeahead,
            C1Select,
            C1Label,
            C1FormInput,
            C1FormGroup,
            C1FormTextarea,
            PayBandMenu,
            C1WhatIsThis,
            C1AppleSwitch,
        },

        props: {
            workExperience: {
                type: Object,
                default: () => ({}),
            },

            hideDescription: {
                type: Boolean,
                default: false,
            },

            payBandMandatory: {
                type: Boolean,
                default: false,
            },

            updateMeDesired: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                payType: 'Hourly',
                occupationQuery: '',
                form: {
                    id: null,
                    job_title: '',
                    classification: {},
                    subclassification: {},
                    employer_name: '',
                    brand_id: null,
                    job_type: {},
                    contract_type: {},
                    location: '',
                    location_id: null,
                    pay_band: {},
                    is_current: false,
                    start_date: '',
                    end_date: '',
                    description: '',
                    log_request_id: '',
                },
                today: '',
                brandOptions: [],
                jobTitleOptions: [],
                locationOptions: [],
                occupationOptions: [],
                jobType: [],
                contractType: [],
                payBandAnnually: [],
                payBandHourly: [],
                searchOccupationLoading: false,
            };
        },

        computed: {
            payBandAnnuallyFiltered() {
                const payBandAnnually = JSON.parse(
                    JSON.stringify(this.payBandAnnually),
                );
                payBandAnnually.forEach(o => {
                    if (o.value > 0) {
                        o.label = this.$t('global.currency_format').format(
                            numeral(o.value).format('0,0'),
                        );
                    }
                });

                return payBandAnnually;
            },

            payBandHourlyFiltered() {
                const payBandHourly = JSON.parse(
                    JSON.stringify(this.payBandHourly),
                );
                payBandHourly.forEach(o => {
                    if (o.value > 0) {
                        o.label = this.$t('global.currency_format').format(
                            numeral(o.value).format('0,0'),
                        );
                    }
                });

                return payBandHourly;
            },

            payBandValidate() {
                return this.payBandMandatory ? 'required|object:id' : '';
            },

            payBandMenuHide() {
                return this.form.job_type.value !== 'Full Time' ? 'Annually' : '';
            },

            showBasePay() {
                return has(this.form.job_type, 'id');
            },
        },

        watch: {
            'form.job_title': {
                handler() {
                    this.getSalaryEstimation();
                },
                deep: true,
            },

            'form.contract_type': {
                handler() {
                    this.getSalaryEstimation();
                    if (this.updateMeDesired) {
                        const attributeId = this.form.contract_type.attribute_id;
                        const data = {
                            attribute_option_ids: [this.form.contract_type.id],
                        };
                        this.updateMeDesiredAttributes(attributeId, data);
                    }
                },
                deep: true,
            },

            'form.subclassification': {
                handler() {
                    this.getSalaryEstimation();
                },
                deep: true,
            },

            'form.location': {
                handler() {
                    this.getSalaryEstimation();
                },
                deep: true,
            },

            'form.job_type': {
                handler(value, oldValue) {
                    if (value.value === 'Full Time') {
                        this.payType = 'Annually';
                    }

                    if (
                        this.payType === 'Annually' &&
                        (value.value === 'Full Time' ||
                            oldValue.value === 'Full Time')
                    ) {
                        this.form.pay_band = {};
                    }

                    this.getSalaryEstimation();
                    if (this.updateMeDesired) {
                        const attributeId = this.form.job_type.attribute_id;
                        const data = {
                            attribute_option_ids: [this.form.job_type.id],
                        };
                        this.updateMeDesiredAttributes(attributeId, data);
                    }
                },
                deep: true,
            },

            'form.is_current'() {
                this.$nextTick(() => {
                    this.form.end_date = '';
                });
            },

            payBandMenuHide(value) {
                if (value === 'Annually') {
                    this.payType = 'Hourly';
                } else if (value === 'Hourly') {
                    this.payType = 'Annually';
                }
            },

            form: {
                handler() {
                    this.onDataChange();
                },
                deep: true,
            },
        },

        created() {
            this.getInitialData();
            this.fillForm();
        },

        mounted() {},

        methods: {
            ...mapActions('attribute', {
                $_getJobType: 'getJobType',
                $_getContractType: 'getContractType',
            }),

            ...mapActions('payType', {
                $_getAnnualSalary: 'getAnnualSalary',
                $_getHourlyRate: 'getHourlyRate',
            }),

            ...mapActions('location', {
                $_getLocations: 'getLocations',
                $_getAllLocations: 'getAllLocations',
            }),

            ...mapActions('occupation', {
                $_getOccupationSuggestions: 'getOccupationSuggestions',
            }),

            ...mapActions('classification', {
                $_getClassificationSuggestions: 'getClassificationSuggestions',
            }),

            ...mapActions('brand', {
                $_getBrands: 'getBrands',
            }),

            ...mapActions('job', {
                $_getJobTitleSuggestion: 'getJobTitleSuggestion',
            }),

            ...mapActions('common', {
                $_getSalaryEstimation: 'getSalaryEstimation',
            }),

            ...mapActions('me', {
                $_syncAttributes: 'syncAttributes',
                $_getAttributes: 'getAttributes',
            }),

            ...mapMutations('me', {
                $_STORE_ATTRIBUTE_OPTION: 'STORE_ATTRIBUTE_OPTION',
            }),

            getInitialData() {
                this.$_getJobType().then(response => {
                    this.jobType = response.data;
                });
                this.$_getContractType().then(response => {
                    this.contractType = response.data;
                });
                this.$_getAnnualSalary().then(response => {
                    this.payBandAnnually = response.data;
                });
                this.$_getHourlyRate().then(response => {
                    this.payBandHourly = response.data;
                });
                this.today = moment().format('yyyy-MM');
            },

            fillForm() {
                if (this.workExperience.id) {
                    this.form.id = this.workExperience.id || null;
                    this.form.job_title = this.workExperience?.job_title || '';
                    this.occupationQuery =
                        this.workExperience?.subclassification?.value || '';
                    this.form.classification =
                        this.workExperience.classification || {};
                    this.form.subclassification =
                        this.workExperience.subclassification || {};
                    this.form.employer_name =
                        this.workExperience?.employer_name || '';
                    this.form.brand_id = this.workExperience?.brand_id | null;
                    this.form.job_type = this.workExperience?.jobType || {};
                    this.form.contract_type =
                        this.workExperience?.contractType || {};
                    this.form.location = this.workExperience?.location?.label || '';
                    this.form.location_id =
                        this.workExperience?.location?.id || null;
                    this.payType =
                        has(this.workExperience, 'payBand.pay_type_id') &&
                        this.workExperience.payBand.pay_type_id === 1
                            ? 'Annually'
                            : 'Hourly';
                    this.form.is_current = this.workExperience.is_current;
                    if (this.workExperience.start_date) {
                        this.form.start_date = moment(
                            this.workExperience.start_date,
                        ).format('yyyy-MM');
                    } else {
                        this.form.start_date = '';
                    }

                    if (this.workExperience.end_date) {
                        this.form.end_date = moment(
                            this.workExperience.end_date,
                        ).format('yyyy-MM');
                    } else {
                        this.form.end_date = '';
                    }

                    this.form.description = this.workExperience.description;
                    this.$nextTick(() => {
                        this.form.pay_band = has(this.workExperience, 'payBand.id')
                            ? this.workExperience.payBand
                            : {};
                    });
                }
            },

            locationSearch(query) {
                this.$_getAllLocations({ query }).then(response => {
                    this.locationOptions = response.data;
                });
            },

            brandSearch(query) {
                this.$_getBrands(query).then(response => {
                    this.brandOptions = response.data;
                });
            },

            jobTitleSearch(query) {
                this.$_getJobTitleSuggestion(query).then(response => {
                    if (response.data.length) {
                        this.jobTitleOptions = response.data.map((o, idx) => {
                            return {
                                ...o,
                                uniq_id: idx,
                            };
                        });
                    }
                });
            },

            onPayTypeSelect() {
                this.form.pay_band = {};
            },

            dateFormatter(date) {
                return moment
                    .utc(date)
                    .local()
                    .format('MMMM YYYY');
            },

            onDataChange() {
                this.$emit('change', this.form);
            },

            getSalaryEstimation() {
                if (
                    has(this.form, 'job_title.label') &&
                    this.form.job_title.label &&
                    has(this.form, 'subclassification.id') &&
                    this.form.subclassification.id &&
                    has(this.form, 'job_type.id') &&
                    this.form.job_type.id &&
                    has(this.form, 'contract_type.id') &&
                    this.form.contract_type.id &&
                    has(this.form, 'location.id') &&
                    this.form.location.id &&
                    !has(this.form, 'pay_band.id')
                ) {
                    const data = {
                        jobTitle: this.form.job_title.label,
                        classificationId: this.form.classification.id,
                        subclassificationId: this.form.subclassification.id,
                        jobTypeId: this.form.job_type.id,
                        contactTypeId: this.form.contract_type.id,
                        locationId: this.form.location.id,
                    };

                    this.$_getSalaryEstimation(data).then(response => {
                        const res = response.data;
                        this.form.log_request_id = res.log_request_id;
                    });
                }
            },

            updateMeDesiredAttributes(attributeId, data) {
                if (attributeId) {
                    this.$_syncAttributes({ id: attributeId, data }).then(() => {
                        this.$_getAttributes().then(resp => {
                            this.$_STORE_ATTRIBUTE_OPTION(resp.data);
                        });
                    });
                }
            },

            onLocationSelected(location) {
                this.form.location_id = location?.id || null;
            },

            onCompanySelected(company) {
                this.form.brand_id = company?.id || null;
            },

            searchOccupations(keyword) {
                this.searchOccupationLoading = true;
                this.$_getClassificationSuggestions({ keyword }).then(response => {
                    this.occupationOptions = response.data;
                    this.searchOccupationLoading = false;
                });
            },

            onOccupationChange(occupations) {
                if (occupations) {
                    this.form.classification = { id: occupations.category_id };
                    this.form.subclassification = { id: occupations.id };
                }
            },
        },
    };
</script>
